<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { NButton, NFormItem, NInput, NModal, useMessage } from 'naive-ui'
import { useListItemsStore } from '../stores/lists'

const props = defineProps<{ show: boolean }>()
const emit = defineEmits<{
  'update:show': [value: boolean]
  created: [list: string]
}>()

const store = useListItemsStore()
const message = useMessage()

const LIST_NAME_MAX_LENGTH = 40
const LIST_NAME_RE = new RegExp(`^[a-z_]{1,${LIST_NAME_MAX_LENGTH}}$`)

const newListName = ref('')
const newListTitle = ref('')
const creatingNewList = ref(false)
const newListNameRef = ref<InstanceType<typeof NInput> | null>(null)
const newListTitleRef = ref<InstanceType<typeof NInput> | null>(null)
const newListNameError = ref<string | null>(null)
const newListTitleError = ref<string | null>(null)

const newListDisplayName = computed(() => (newListName.value ? displayName(newListName.value) : ''))

function displayName(list: string): string {
  return list.replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase())
}

// Reset the form each time the modal re-opens so a previous attempt's
// name/title/errors never carries over.
watch(
  () => props.show,
  (open) => {
    if (!open) return
    newListName.value = ''
    newListTitle.value = ''
    newListNameError.value = null
    newListTitleError.value = null
  },
)

function coerceListName(v: string) {
  newListName.value = v
    .toLowerCase()
    .replaceAll(/[^a-z_]/g, '_')
    .replaceAll(/_+/g, '_')
    .slice(0, LIST_NAME_MAX_LENGTH)
  newListNameError.value = null
}

async function createNewList() {
  const name = newListName.value.trim()
  const title = newListTitle.value.trim()
  if (!name) {
    newListNameError.value = 'List name is required'
    return
  }
  if (!LIST_NAME_RE.test(name)) {
    newListNameError.value = `Lowercase letters and underscores only (max ${LIST_NAME_MAX_LENGTH} chars)`
    return
  }
  if (!title) {
    newListTitleError.value = 'First item is required'
    return
  }
  creatingNewList.value = true
  try {
    await store.addItem({ list: name, title })
    emit('update:show', false)
    emit('created', name)
    message.success(`Created "${displayName(name)}" list`)
  } catch {
    message.error('Failed to create list')
  } finally {
    creatingNewList.value = false
  }
}
</script>

<template>
  <NModal
    :show="props.show"
    preset="card"
    title="New list"
    style="width: min(420px, calc(100vw - 32px))"
    :mask-closable="true"
    @update:show="(v: boolean) => emit('update:show', v)"
    @after-enter="newListNameRef?.focus()"
  >
    <div class="flex flex-col gap-4">
      <p class="m-0 text-[13px] text-ink-muted">Lists are created with at least one item — you can add more after.</p>
      <NFormItem
        label="List name"
        :validation-status="newListNameError ? 'error' : undefined"
        :feedback="newListNameError ?? undefined"
      >
        <NInput
          ref="newListNameRef"
          :value="newListName"
          placeholder="e.g. apartment"
          @update:value="coerceListName"
          @keydown.enter.prevent="newListTitleRef?.focus()"
        />
      </NFormItem>
      <p v-if="newListDisplayName" class="m-0 -mt-3 text-xs text-ink-faint">Displayed as: {{ newListDisplayName }}</p>
      <NFormItem
        label="First item"
        :validation-status="newListTitleError ? 'error' : undefined"
        :feedback="newListTitleError ?? undefined"
      >
        <NInput
          ref="newListTitleRef"
          v-model:value="newListTitle"
          placeholder="e.g. Buy paint"
          @update:value="newListTitleError = null"
          @keydown.enter.prevent="createNewList()"
        />
      </NFormItem>
      <div class="flex gap-2">
        <NButton type="primary" :loading="creatingNewList" @click="createNewList()">Create list</NButton>
        <NButton quaternary @click="emit('update:show', false)">Cancel</NButton>
      </div>
    </div>
  </NModal>
</template>
